import AlertBox from 'modules/Business/components/Funding/AlertBox';
import {
  Box,
  Button,
  Chip,
  theme,
  Typography,
  Dropdown,
  DropdownContext,
  DropdownToggle,
  DropdownList,
  DropdownItem,
  IconBolt,
  IconExpandMore,
  IconExpandLess,
  IconInfoOutlined,
  IconBank,
  Alert,
  IconChatBubbleOutline,
} from '@capitalise/ui-design-system';
import { ProductTypeLabel } from 'enums/product-type';
import {
  FundingMatchStatus,
  FundingMatchStatusLabel,
  FundingMatchStatusColor,
  FundingMatchStatusIcon,
  FundingMatchStatusVariant,
} from 'enums/funding-match-status';
import { cloneElement } from 'react';
import ProductBuilder from 'modules/Business/components/Funding/ProductBuilder';
import MatchStepper from 'modules/Business/components/Funding/MatchStepper';
import useMediaQuery from 'modules/Business/hooks/useMediaQuery';
import { openModal } from 'reducers/modal/actions';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { GapFlexBox, slideRightCss, StyledHr } from './styled';
import PropTypes from 'prop-types';
import MatchCardButtons from 'modules/Business/components/Funding/MatchCardButtons';
import { downloadUrl } from 'utils/Download';

const fundingMatchStatusHasChip = [
  FundingMatchStatus.awaitingClientDecision,
  FundingMatchStatus.clientDecline,
  FundingMatchStatus.clientAccepted,
  FundingMatchStatus.lenderDecline,
  FundingMatchStatus.lenderRejected,
];

const stepsInError = [
  FundingMatchStatus.passed,
  FundingMatchStatus.lenderDecline,
  FundingMatchStatus.lenderRejected,
  FundingMatchStatus.lost,
  FundingMatchStatus.cancelled,
  FundingMatchStatus.clientDecline,
  FundingMatchStatus.nonResponsive,
];

const canResubmitStatuses = [FundingMatchStatus.lenderRejected, FundingMatchStatus.incomplete];

const noButtonsStatuses = [FundingMatchStatus.clientAccepted, FundingMatchStatus.paidOut];

export const MatchCard = ({
  id,
  logo,
  status,
  hasOffer,
  name,
  product,
  documents,
  hasInstantOffer,
  onDropdownClick,
  publicComment,
  openModal,
  offerId,
  index,
  statusComment,
  ...props
}) => {
  const { isMobile } = useMediaQuery();

  const { fundingSearchId } = useParams();

  const inError = stepsInError.includes(status);
  const hasResponse =
    status === FundingMatchStatus.lenderDecline ||
    status === FundingMatchStatus.lenderRejected ||
    !!publicComment;

  const hasOfferWithoutError = !inError && hasOffer;

  return (
    <Box
      position="relative"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      backgroundColor="white"
      p={{ md: 'medium', default: 'xsmall' }}
      borderRadius={theme.sizes['xsmall']}
      marginBottom="3xsmall"
      {...(fundingMatchStatusHasChip.includes(status) && {
        border: `2px solid ${
          hasOffer ? theme.colors[FundingMatchStatusColor[status]] : 'transparent'
        }`,
      })}
      css={slideRightCss(index)}
      data-testid="match-card"
    >
      {fundingMatchStatusHasChip.includes(status) && (
        <Box
          position="absolute"
          top={hasOffer ? '-13px' : '-14px'}
          left="medium"
          as="span"
          display="flex"
          alignItems="center"
          paddingX="xtiny"
        >
          <Chip
            baseColor={FundingMatchStatusColor[status].split('-')[0]}
            leftIcon={cloneElement(FundingMatchStatusIcon[status], {
              width: '3xsmall',
              color: 'white',
            })}
            round
            size="xsmall"
            variant={FundingMatchStatusVariant[status]}
          >
            {FundingMatchStatusLabel[status]}
          </Chip>
        </Box>
      )}
      <Box
        display="grid"
        gridTemplateColumns={{ md: '1fr 3fr 1fr', default: '1fr' }}
        alignItems="center"
        gridGap="4xsmall"
      >
        <Box display="flex" justifyContent="flex-start">
          <Box as="img" src={logo} alt="lenderLogo" maxWidth="120px" />
        </Box>
        <Box display="flex" width="100%">
          <MatchStepper status={status} product={product} />
        </Box>
        {!isMobile && (
          <MatchCardButtons
            status={status}
            showButtons={hasOfferWithoutError && !noButtonsStatuses.includes(status)}
            showResubmitButton={hasInstantOffer && canResubmitStatuses.includes(status)}
            isMobile={isMobile}
            onButtonClick={onDropdownClick}
            name={name}
          />
        )}
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" marginY="xsmall">
        <Typography size="heading-14">{`${name} ${hasResponse ? 'response' : 'offer'}`}</Typography>
        <Box flex="1" pl="3xsmall">
          <Box height="1px" backgroundColor="grey-100" />{' '}
        </Box>
      </Box>
      <Box
        display="grid"
        gridTemplateColumns={{ md: inError ? '' : '1fr 3fr 1fr', default: '' }}
        alignItems="center"
        gridGap={{ md: '4xsmall', default: 'tiny' }}
      >
        <GapFlexBox display="flex" flexWrap="wrap" pt={{ md: '0', default: '3xsmall' }}>
          <Chip size="small" key={product} round baseColor="grey" leftIcon={<IconBank />}>
            <Typography size="caption-12" color="grey">
              {ProductTypeLabel[product]}
            </Typography>
          </Chip>
          {hasInstantOffer && (
            <Chip size="small" baseColor="blue" round variant="filled" leftIcon={<IconBolt />}>
              <Typography size="caption-12" color="white">
                Instant offer
              </Typography>
            </Chip>
          )}
        </GapFlexBox>

        {hasOfferWithoutError && <ProductBuilder product={product} {...props} />}

        {!inError && !hasOffer && (
          <Box p="3xsmall" display="flex" alignItems="center">
            <Box pr="3xsmall">
              <IconInfoOutlined color="grey-500" />
            </Box>
            <Box flex="1">
              <Typography size="body-16">
                This lender has not provided a response yet. We&apos;ll update you soon.
              </Typography>
            </Box>
          </Box>
        )}

        {hasOfferWithoutError && (
          <GapFlexBox
            display="flex"
            justifyContent={{ md: 'space-between', default: '' }}
            flexWrap="wrap"
            pt={{ md: '0', default: '3xsmall' }}
          >
            <>
              {documents?.length > 0 && (
                <Box pr="xtiny">
                  <Dropdown>
                    <DropdownContext.Consumer>
                      {({ open }) => (
                        <DropdownToggle>
                          <Button size="small" baseColor="green" variant="text">
                            {documents.length} document{documents.length === 1 ? '' : 's'}
                            {open ? (
                              <IconExpandLess color="green-500" />
                            ) : (
                              <IconExpandMore color="green-500" />
                            )}
                          </Button>
                        </DropdownToggle>
                      )}
                    </DropdownContext.Consumer>
                    <DropdownList>
                      {documents.map((document) => (
                        <DropdownItem
                          key={document.id}
                          onClick={() => downloadUrl(document.url, document.name)}
                        >
                          <Typography size="body-14">{document.name}</Typography>
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  </Dropdown>
                </Box>
              )}
              <Button
                variant="outlined"
                baseColor="green"
                size="small"
                onClick={() =>
                  openModal({
                    fundingSearchId,
                    offerId,
                    matchId: id,
                    status,
                  })
                }
              >
                View offer
              </Button>
            </>
          </GapFlexBox>
        )}

        {isMobile && (
          <Box>
            <StyledHr />

            <Box pt="3xsmall">
              <MatchCardButtons
                status={status}
                showButtons={hasOfferWithoutError && !noButtonsStatuses.includes(status)}
                showResubmitButton={hasInstantOffer && canResubmitStatuses.includes(status)}
                isMobile={isMobile}
                onButtonClick={onDropdownClick}
                name={name}
              />
            </Box>
          </Box>
        )}
      </Box>
      <AlertBox
        description="Unfortunately the lender has declined your application. Your application does not meet the
            lender’s criteria."
        show={status === FundingMatchStatus.lenderDecline}
        variant="danger"
      />

      <AlertBox
        description="Incorrect percent_shares_held, EBOs must have 50% or more ownership of the applicant
          company, with at least one EBO holding 20% or more shares."
        show={status === FundingMatchStatus.lenderRejected}
        variant="warning"
      />
      <AlertBox description={publicComment} show={!!publicComment} variant="info" />

      {statusComment && (
        <Box pt="tiny">
          <Alert type="info" icon={IconChatBubbleOutline}>
            <Box dangerouslySetInnerHTML={{ __html: statusComment.replace(/\n/g, '<br>') }} />
          </Alert>
          <Typography pt="tiny" size="caption-12" color="grey-700">
            A note has been left on your application.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({
  openModal: (modalProps) => dispatch(openModal('offer', modalProps)),
});

MatchCard.propTypes = {
  id: PropTypes.number,
  logo: PropTypes.string,
  status: PropTypes.number,
  hasOffer: PropTypes.bool,
  name: PropTypes.string,
  product: PropTypes.number,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  hasInstantOffer: PropTypes.bool,
  onDropdownClick: PropTypes.func,
  publicComment: PropTypes.string,
  openModal: PropTypes.func,
  offerId: PropTypes.string,
  index: PropTypes.number.isRequired,
  statusComment: PropTypes.string,
};

export default connect(null, mapDispatchToProps)(MatchCard);
